import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import dayjs from "dayjs";
import { Row, Cell, Card, Icon, Section, Tabs } from "@sunwing/components";
import { RCLContext } from "../../components/RCL/RCLProvider";
import { RCL } from "../../components/RCL";
import { useRouting } from "../../hooks";
import { cloudinaryImage } from "../../utils/cloudinary";
import { Loader } from "../../components/Loader";
import moment from "moment";
import { Breadcrumb } from "../../components/Breadcrumb";
import { useOktaAuth } from "@okta/okta-react";
import config from "../../app/config";
import { Link } from 'react-router-dom';
import { VWQIcon } from "@sunwing/icons";

let styles;

(async () => {
  if (config.isWestJetMode) {
    styles = await import("./flyersVwq.module.scss");
  } else {
    styles = await import("./flyers.module.scss");
  }
})();

const Flyers = () => {
  const { locale } = useContext(RCLContext);
  const { setCurrentRoute } = useRouting();

  const [tabIndex, setTabIndex] = useState("tabfly-0");
  const { oktaAuth } = useOktaAuth();
  const [flyersInfo, setFlyersInfo] = useState(null);

   const predefinedCategories = [
    "Promotions/Promotions",
    "Added Value/Valeur ajoutée",
    "Featured partners/Partenaires en vedette",
    "STAR Points/Points STAR",
    "Groups & Weddings/Groupes et mariages"
  ];
  
  const getFlyersInfo = async () => {
    const query =`
    query flyersData($locale: String, $skip:Int, $currentDate:DateTime, $brandCode:String!, $categories:[String!]) 
    {
      flyersCollection(
        locale: $locale, limit:100, skip:$skip, 
        where:{expiryDate_gte:$currentDate, brand: {brandCode:$brandCode}, 
          category_contains_some: $categories
        }
      )
      {
        items {
          sys {
            id
          }
          displayTitle
          promotionType
          flyerFileCollection {
            items {
              url
              size
              description
            }
          }
          startDate
          expiryDate
          category
          flyerImageAsset
          landingPage {
            sys {
              id
            }
            slug
            landingPageLinkText
          }
        }
      }
    }
  `;

  await fetch(config.resourceServer.contentfulApimAgentGqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${oktaAuth.getAccessToken()}`,
      },
      body: JSON.stringify({
        query: query,
        variables: { 
          locale: locale === "fr" ? locale : "en-CA", 
          "skip":0,
          "currentDate":(new Date()).toISOString().substring(0, 10),
          brandCode:config.brand ? config.brand.toUpperCase() : 'SWG',
          categories: predefinedCategories
        },
      }),
    })
      .then((response) => response.json())
      .then(({ data, errors }) => {
        if (errors) {
          console.error(errors);
        }else{
          setFlyersInfo(data?.flyersCollection?.items);
        }
      });
  };

  const flyerCategories =
  flyersInfo && !!flyersInfo.length &&
    flyersInfo
      .map((flyer) => flyer.category[0])
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort((a, b)=> ( predefinedCategories.indexOf(a) - predefinedCategories.indexOf(b)));

  const flyersWithTabsData =
    flyerCategories &&
    flyerCategories.map((category, index) => {
      return {
        key: `tabfly-${index}`,
        panel: (
          <>
            {!flyersInfo && <Loader />}
            {flyersInfo !== null && (
              <div>
                <h2>
                  {locale === "fr"
                    ? category.split("/")[1]
                    : category.split("/")[0]}
                </h2>
                <Row cols={3}>
                  {flyersInfo
                    ?.slice()
                    .sort((a, b) => ((a.startDate !== null) && -a.startDate.localeCompare(b.startDate)))
                    .filter((fly) => fly.category[0] === category)
                    .map(
                      (flyer) =>                
                        flyer?.flyerImageAsset !== null 
                        && ((flyer?.startDate==null) || ((flyer?.startDate !==null) && (moment().isAfter(flyer?.startDate))))
                        && flyer?.expiryDate!==null && moment().isBefore(flyer?.expiryDate) && (                          
                        <Cell key={flyer.displayTitle}>
                            <Card>
                              <Card.Image
                                className={styles.flyersImage}
                                src={cloudinaryImage(
                                  flyer?.flyerImageAsset?.[0]?.public_id,
                                  {
                                    width: 747,
                                  }
                                )}
                                alt="placeholder alt"
                              />
                              <Card.Body>
                                <p className={ styles.flyersPostDate }>
                                  {flyer.startDate ? dayjs(flyer.startDate, "YYYY-MM-D").format(
                                    "MMM D, YYYY"
                                  ) : <br/>}
                                </p>
                                <Card.Header
                                  as="p"
                                  size="p"
                                  weight="bold"
                                  className={styles.flyersCardHeading}
                                >
                                  {flyer.displayTitle}
                                </Card.Header>
                                <div  className={styles.flyersCardLinkContainer}>
                                  {flyer.flyerFileCollection && flyer.flyerFileCollection?.items[0] && (
                                    <Card.Link
                                      className={ styles.flyersLink}
                                      target="_blank"
                                      to={flyer.flyerFileCollection.items[0].url}
                                    >
                                      <Icon
                                        className={styles.flyersDownloadLink}
                                        name="download"
                                        aria-hidden="true"
                                      />
                                      {RCL({ searchKey: "AgentTools_Download" })}
                                    </Card.Link>)
                                  }
                                  {flyer.landingPage && flyer?.landingPage?.sys.id && (
                                    <Link
                                      className={ styles.flyersLandingPageLink}
                                      target="_blank"
                                      to={`/${locale}/LandingPage?brand=${config.brand}&slug=${flyer?.landingPage?.slug}`}
                                    >
                                      {flyer?.landingPage?.landingPageLinkText}
                                      <VWQIcon
                                        className={styles.flyersLandingPageLinkIcon}
                                        name="arrow-right-micro"
                                        aria-hidden="true"
                                      />
                                    </Link>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </Cell>
                        )
                      )}
                </Row>
              </div>
            )}
          </>
        ),
        toggle: {
          iconComponent: <Icon className="custom-icon" name="children" />,
          label: {
            heading:
              locale === "fr" ? category.split("/")[1] : category.split("/")[0],
          },
        },
      };
    });

  useEffect(() => {
    if (setCurrentRoute) {
      setCurrentRoute({
        en: { path: "/en/flyers" },
        fr: { path: "/fr/flyers" },
      });
    }
    getFlyersInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div className={styles.flyers}>
      <Helmet>
        <title>{`Agent Portal - ${RCL({ searchKey: "Flyers_header" })}`}</title>
      </Helmet>
      {!config.isWestJetMode && flyersInfo && (
         <Breadcrumb pageSlug={"flyers"} locale={locale} />
      )}
      <Section
        title={RCL({ searchKey: "Flyers_header" })}
        titleAlignment="left"
        titleUppercase={false}
      >
        <p className={styles.flyersDesc}>
        {config.isWestJetMode ? <RCL searchKey="FlyersVwq_Desc" /> : <RCL searchKey="Flyers_Desc" />}
        </p>
        
        {flyersWithTabsData && (
          <Tabs
            activeKey={tabIndex}
            data={flyersWithTabsData}
            tabIconLeftComponent={<Icon name="arrow-left" />}
            tabIconRightComponent={<Icon name="arrow-right" />}
            theme="generic"
            customTabWrapperClass="tabsWrapper"
            customPanelClass="tabsPanel"
            customActiveClass="tabsActive"
            className={styles.flyers}
          />
        )}
      </Section>
    </div>
  );
};

export default Flyers;
